import React from "react";
import TransportationActions from "../../transportationActions/transportationActions";
import {RouteComponentProps, withRouter} from "react-router";
import {RouteParamsModel} from "../../../../common/models/routeParams";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import SimpleInfoElement from "../../../../common/components/shared/simpleInfoElement/simpleInfoElement";
import {Chip} from "@mui/material";
import "./orderStatus.scss";
import {DepoFlowActions, CurrentStatusModel, OrderStatusEnum} from "../../../models/order";
import {Api} from "../../../../helpers/api";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import i18n from "../../../../i18n/i18n";
import Loader from "../../../../common/components/shared/loader/loader";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import {errorMessages} from "../../../../helpers/errorMessage";
import {storesContext} from "../../../../common/stores/stores";
import {observer} from "mobx-react";
import {OrderService} from "../../../services/orderService";
import {ReactComponent as AllowIcon} from "../../../../statics/icons/icon_allow.svg";
import Button from "../../../../common/components/shared/button/button";
import AllowWithLocationDepoPopup from "./allowWithLocationDepoPopup/allowWithLocationDepoPopup";
import AllowWithTcrRequiredPopup from "./allowWithTcrRequiredPopup/allowWithTcrRequiredPopup";
import {IUserStore} from "../../../../administration/stores/userStore";
import RoutingUtils from "../../../../helpers/routingUtils";
import {IOrderStore} from "../../../stores/orderStore";

enum TypeOfPopup {
    discardPopup = "discardPopup",
    allowWithLocationDepoPopup = "allowWithLocationDepoPopup",
    allowWithTcrPopup = "allowWithTcrPopup"
}

interface OrderStatusState {
    orderId: string;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    statusData?: CurrentStatusModel | null;
    displayPopup: TypeOfPopup | null;
    discardTransportResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    allowTransportResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    selectedLocation: ValidatedValue<string>;
}

const notEmptyValidator = {
    name: "valueIsNotEmpty",
    fn: Validator.isNotEmpty,
    errorMessage: errorMessages.required
};

class OrderStatus extends React.Component <RouteComponentProps, OrderStatusState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            orderId: "",
            response: undefined,
            statusData: null,
            displayPopup: null,
            discardTransportResponse: undefined,
            allowTransportResponse: undefined,
            selectedLocation: new ValidatedValue("", [notEmptyValidator])
        };
    }

    private get statusData(): CurrentStatusModel | null {
        return this.state.statusData || null;
    }

    async componentDidMount() {
        const params = this.props.match.params as RouteParamsModel;
        const response = await OrderService.getOrderStatus(params.id);

        this.setState({
            orderId: params.id,
            response,
            statusData: Api.isSuccess(response) ? response?.data : null
        });
    }

    public render(): JSX.Element {
        const orderStore = this.context.orderStore as IOrderStore;

        return (
            <div className="transportation-order-status">
                <div className="content transportation-order-content">
                    <TransportationActions
                        orderId={this.state.orderId || ""}
                        element="order"
                        relationType={orderStore.relationType.value}
                    />
                    {this.renderContent()}
                </div>
            </div>
        );
    }

    private renderContent(): JSX.Element {
        if (!Api.isSuccess(this.state.response)) {
            return <HttpError
                response={this.state.response}
                cause={i18n.t("transportation.order.status.retrievalErrorSuffix")}
            />;
        } else if (this.statusData) {
            return this.renderOrderStatus();
        } else {
            return (
                <Loader/>
            );
        }
    }

    private renderOrderStatus(): JSX.Element {
        const fillOutTcrAction = this.state.statusData!.primaryAction === DepoFlowActions.FILL_OUT_TCR;

        return (
            <div className="transportation-order-status-sub-content">
                <div className="header" data-testid="header">
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="order-status-reload-btn"
                    />
                </div>
                <div className="section section-status">
                    <div className="section-content">
                        <Chip
                            className="chip actual"
                            label={i18n.t("transportation.order.status.actualChipLabel")}
                        />
                        <SimpleInfoElement
                            label=""
                            value={`${i18n.t(`orderStatus.${this.statusData?.currentStatus}`)}`}
                            type="box"
                            testId="actual-status"
                            classNameContent="actual"
                        />
                    </div>
                    <div className="long-arrow"/>
                    <div className="section-content">
                        <Chip
                            className="chip next"
                            label={i18n.t("transportation.order.status.nextChipLabel")}
                        />
                        <SimpleInfoElement
                            label=""
                            value={this.statusLabel(this.statusData?.nextStatus)}
                            type="box"
                            testId="next-status"
                            classNameContent="next"
                        />
                    </div>
                </div>
                <div className="section">
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.status.currentRouteLabel")}
                        value={`${i18n.t(`relationType.${this.statusData?.relationType}`)}`}
                        type="box"
                        testId="current-route"
                        className="in-border"
                    />
                </div>
                <div className="section actions">
                    {this.renderActions(this.statusData!.primaryAction)}
                </div>
                {this.renderAllowPopup(this.state.displayPopup)}
                {!fillOutTcrAction && !Api.isSuccess(this.state.allowTransportResponse) &&
                    <HttpError
                        response={this.state.allowTransportResponse}
                        cause={i18n.t("transportation.order.status.allowErrorSuffix")}
                    />
                }
            </div>
        );
    }

    private statusLabel(status: OrderStatusEnum | undefined) {
        if (status) {
            return `${i18n.t(`orderStatus.${status}`)}`;
        } else {
            return i18n.t("transportation.order.status.noStatus");
        }
    }

    private renderActions(action: DepoFlowActions | null) {
        switch (action) {
            case DepoFlowActions.ENTER_PORT:
            case DepoFlowActions.EXIT_DEPO:
            case DepoFlowActions.ENTER_FERRY:
            case DepoFlowActions.EXIT_FERRY:
                return this.renderAllowButton(this.confirmAllowTransport.bind(this, action));
            case DepoFlowActions.ENTER_DEPO:
                return this.renderAllowButton(this.onAllowWithLocationDepoAction.bind(this));
            case DepoFlowActions.FILL_OUT_TCR:
                return this.renderAllowButton(this.onAllowWithTcrRequiredAction.bind(this));
            default:
                break;
        }
    }

    private renderAllowPopup(type: TypeOfPopup | null) {
        switch (type) {
            case TypeOfPopup.allowWithLocationDepoPopup:
                return this.renderAllowWithLocationDepoPopup();
            case TypeOfPopup.allowWithTcrPopup:
                return this.renderAllowWithTcrRequiredPopup();
            // TODO: Uncomment and modify in next steps
            // case TypeOfPopup.discardPopup:
            //     return this.renderDiscardPopup();
            default:
                break;
        }
    }

    private renderAllowButton(onClick: any): JSX.Element {
        return (
            <Button
                className="allow-btn"
                text={i18n.t("transportation.order.status.actions.allow")}
                type="secondary"
                testId="allow-button"
                endIcon={
                    <AllowIcon/>
                }
                onClick={onClick}
            />
        );
    }

    private renderAllowWithLocationDepoPopup() {
        return (
            <AllowWithLocationDepoPopup
                displayPopup={this.state.displayPopup === TypeOfPopup.allowWithLocationDepoPopup}
                closePopup={this.closeAllowWithLocationDepoPopup.bind(this)}
                confirm={this.confirmAllowWithLocationDepoTransport.bind(this)}
                response={this.state.allowTransportResponse}
                orderId={this.state.orderId}
                selectedLocation={this.state.selectedLocation}
                onChangeDropdown={(newValue: any) => {
                    this.state.selectedLocation.setValue(newValue);
                }}
            />
        );
    }

    private closeAllowWithLocationDepoPopup() {
        this.setState({
            displayPopup: null,
            discardTransportResponse: undefined,
            allowTransportResponse: undefined,
            selectedLocation: new ValidatedValue("", [notEmptyValidator])
        });
    }

    private onAllowWithLocationDepoAction() {
        this.setState({displayPopup: TypeOfPopup.allowWithLocationDepoPopup});
    }

    // TODO: Uncomment and modify in next steps
    // private renderDiscardButton(): JSX.Element {
    //     return (
    //         <Button
    //             className="discard-btn"
    //             text={i18n.t("transportation.order.status.actions.discard")}
    //             type="secondary"
    //             testId="discard-button"
    //             endIcon={
    //                 <DiscardIcon/>
    //             }
    //             onClick={this.onDiscardAction.bind(this)}
    //         />
    //     );
    // }

    // private renderDiscardPopup() {
    //     return (
    //         <DiscardPopup
    //             displayPopup={this.state.displayPopup === TypeOfPopup.discardPopup}
    //             closePopup={this.closeDiscardPopup.bind(this)}
    //             confirm={this.confirmDiscardTransport.bind(this)}
    //             response={this.state.discardTransportResponse}
    //         />
    //     );
    // }

    // private closeDiscardPopup() {
    //     this.setState({
    //         displayPopup: null,
    //         discardTransportResponse: undefined,
    //         allowTransportResponse: undefined
    //     });
    // }

    // private onDiscardAction() {
    //     this.setState({displayPopup: TypeOfPopup.discardPopup});
    // }

    private renderAllowWithTcrRequiredPopup() {
        return (
            <AllowWithTcrRequiredPopup
                displayPopup={this.state.displayPopup === TypeOfPopup.allowWithTcrPopup}
                goToTcr={this.goToTcr.bind(this)}
                closePopup={this.closeAllowWithTcrRequiredPopup.bind(this)}
                confirm={this.confirmAllowWithTcr.bind(this, this.state.statusData!.secondaryAction)}
                response={this.state.allowTransportResponse}
            />
        );
    }

    private closeAllowWithTcrRequiredPopup() {
        this.setState({
            displayPopup: null,
            discardTransportResponse: undefined,
            allowTransportResponse: undefined
        });
    }

    private goToTcr() {
        const userStore = this.context.userStore as IUserStore;

        this.closeAllowWithTcrRequiredPopup();

        this.props.history.push(RoutingUtils.getTcrAddress(userStore, this.state.orderId));
    }

    private onAllowWithTcrRequiredAction() {
        this.setState({displayPopup: TypeOfPopup.allowWithTcrPopup});
    }

    // private async confirmDiscardTransport() {
    //     await this.confirm(DepoFlowActions.DISCARD, (response) => this.setState({
    //         discardTransportResponse: response
    //     }));
    // }

    private async confirmAllowWithTcr(action: DepoFlowActions | null) {
        switch (action) {
            case DepoFlowActions.ENTER_DEPO:
                return this.onAllowWithLocationDepoAction();
            case DepoFlowActions.EXIT_DEPO:
            case DepoFlowActions.EXIT_FERRY:
                return this.confirmAllowTransport(action);
            default:
                break;
        }
    }

    private async confirmAllowWithLocationDepoTransport() {
        this.state.selectedLocation.validate();

        if (this.state.selectedLocation.isValid) {
            await this.confirm(DepoFlowActions.ENTER_DEPO, (response) => this.setState({
                allowTransportResponse: response
            }));
        }
    }

    private async confirmAllowTransport(action: DepoFlowActions) {
        await this.confirm(action, (response) => this.setState({
            allowTransportResponse: response
        }));
    }

    private async confirm(action: DepoFlowActions, setStateAction: (response: AxiosResponse<HttpErrorModel[] | any> | undefined) => void) {
        this.setState({
            allowTransportResponse: undefined,
            discardTransportResponse: undefined
        });
        let response;

        if (action === DepoFlowActions.ENTER_DEPO) {
            response = await OrderService.changeOrderStatus(this.state.orderId, action, this.state.selectedLocation.value);
        } else {
            response = await OrderService.changeOrderStatus(this.state.orderId, action);
        }

        setStateAction(response);

        if (Api.isSuccess(response)) {
            await this.reload();
        }
    }

    private async reload() {
        this.setState({statusData: undefined, allowTransportResponse: undefined});
        const response = this.state.orderId ? await OrderService.getOrderStatus(this.state.orderId) : undefined;

        this.setState({
            statusData: Api.isSuccess(response) ? response?.data : null,
            response,
            displayPopup: null
        });
    }
}

export default withRouter(observer(OrderStatus));